import React, { useEffect, useState } from "react";
import { api } from "../api";

export default function Admin() {
  const [pwd, setPwd] = useState(localStorage.getItem("beaudoin_admin") || "");
  const [authed, setAuthed] = useState(!!localStorage.getItem("beaudoin_admin"));
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [error, setError] = useState("");

  const headers = { "x-admin-password": pwd };

  const login = async (e) => {
    e.preventDefault();
    setError("");
    try {
      await api.post("/admin/login", { password: pwd });
      localStorage.setItem("beaudoin_admin", pwd);
      setAuthed(true);
    } catch {
      setError("Invalid password");
    }
  };

  const logout = () => { localStorage.removeItem("beaudoin_admin"); setAuthed(false); setPwd(""); };

  const loadAll = async () => {
    const [pRes, mRes] = await Promise.all([
      api.get("/products"),
      api.get("/admin/messages", { headers }),
    ]);
    setProducts(pRes.data);
    setMessages(mRes.data);
  };

  useEffect(() => { if (authed) loadAll().catch(logout); }, [authed]);

  const saveProduct = async (data) => {
    if (data.id) {
      await api.put(`/admin/products/${data.id}`, data, { headers });
    } else {
      await api.post("/admin/products", data, { headers });
    }
    setEditing(null);
    loadAll();
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete?")) return;
    await api.delete(`/admin/products/${id}`, { headers });
    loadAll();
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-6">
        <form onSubmit={login} className="w-full max-w-sm" data-testid="admin-login-form">
          <div className="text-xs font-bold tracking-[0.3em] uppercase text-blood mb-4">Admin</div>
          <h1 className="font-black text-4xl uppercase mb-10">Restricted</h1>
          <input data-testid="admin-password-input" type="password" value={pwd} onChange={(e) => setPwd(e.target.value)}
            placeholder="Password" autoFocus
            className="w-full bg-transparent border-b border-white/20 focus:border-blood outline-none py-3 mb-6" />
          {error && <div className="text-blood text-sm mb-4">{error}</div>}
          <button data-testid="admin-login-submit" type="submit"
            className="w-full bg-blood text-white py-4 text-xs font-bold tracking-[0.2em] uppercase hover:bg-blood-deep">
            Enter
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-32">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between mb-12">
          <h1 className="font-black text-4xl md:text-5xl uppercase tracking-tight">Admin</h1>
          <button data-testid="admin-logout" onClick={logout} className="text-xs font-bold tracking-[0.2em] uppercase text-white/40 hover:text-white">
            Logout
          </button>
        </div>

        <div className="flex gap-3 mb-10 border-b border-white/10 pb-4">
          {[
            { k: "products", label: `Products (${products.length})` },
            { k: "messages", label: `Messages (${messages.length})` },
          ].map((tt) => (
            <button key={tt.k} onClick={() => setTab(tt.k)}
              data-testid={`admin-tab-${tt.k}`}
              className={`px-4 py-2 text-xs font-bold tracking-[0.2em] uppercase ${
                tab === tt.k ? "bg-blood text-white" : "text-white/40 hover:text-white"
              }`}>
              {tt.label}
            </button>
          ))}
        </div>

        {tab === "products" && (
          <>
            <button data-testid="admin-new-product"
              onClick={() => setEditing({ name: "", name_fr: "", description: "", description_fr: "", price: 0, image: "", category: "tops", sizes: [], shopify_variant_id: "", shopify_handle: "", sumup_link: "", featured: false, in_stock: true })}
              className="mb-8 bg-white text-black px-6 py-3 text-xs font-bold tracking-[0.2em] uppercase hover:bg-bone">
              + New product
            </button>

            {editing && <ProductForm initial={editing} onSave={saveProduct} onCancel={() => setEditing(null)} />}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
              {products.map((p) => (
                <div key={p.id} className="border border-white/10 p-4 flex gap-4">
                  <img src={p.image} alt={p.name} className="w-20 h-24 object-cover" />
                  <div className="flex-1">
                    <div className="font-bold">{p.name}</div>
                    <div className="text-xs text-bone">€{p.price}</div>
                    <div className="text-[10px] text-white/40 uppercase tracking-wide mt-1">{p.category}</div>
                    <div className="text-[10px] text-white/40 mt-1">Variant ID: {p.shopify_variant_id || "—"}</div>
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => setEditing(p)} className="text-xs underline text-white/70 hover:text-white">Edit</button>
                      <button onClick={() => deleteProduct(p.id)} className="text-xs underline text-blood">Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "messages" && (
          <div className="space-y-4">
            {messages.length === 0 && <div className="text-white/40">No messages.</div>}
            {messages.map((m) => (
              <div key={m.id} className="border border-white/10 p-6">
                <div className="flex justify-between mb-2">
                  <div className="font-bold">{m.name}</div>
                  <div className="text-xs text-white/40">{new Date(m.created_at).toLocaleString()}</div>
                </div>
                <a href={`mailto:${m.email}`} className="text-xs text-bone link-hover">{m.email}</a>
                <p className="mt-3 text-sm text-white/70 whitespace-pre-wrap">{m.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ProductForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState({ ...initial, sizes: (initial.sizes || []).join(",") });
  const submit = (e) => {
    e.preventDefault();
    onSave({ ...f, price: parseFloat(f.price) || 0, sizes: f.sizes.split(",").map((s) => s.trim()).filter(Boolean) });
  };
  const fld = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} className="bg-[#0a0707] border border-blood p-6 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Inp label="Name (EN)" v={f.name} on={fld("name")} />
        <Inp label="Nom (FR)" v={f.name_fr} on={fld("name_fr")} />
        <Inp label="Image URL" v={f.image} on={fld("image")} full />
        <Inp label="Price (EUR)" v={f.price} on={fld("price")} type="number" />
        <Inp label="Category" v={f.category} on={fld("category")} />
        <Inp label="Sizes (comma)" v={f.sizes} on={fld("sizes")} />
        <Inp label="Shopify Variant ID" v={f.shopify_variant_id || ""} on={fld("shopify_variant_id")} />
        <Inp label="Shopify Handle" v={f.shopify_handle || ""} on={fld("shopify_handle")} />
        <Inp label="SumUp Payment Link" v={f.sumup_link || ""} on={fld("sumup_link")} full />
      </div>
      <Inp label="Description (EN)" v={f.description} on={fld("description")} full multi />
      <Inp label="Description (FR)" v={f.description_fr} on={fld("description_fr")} full multi />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} />
        Featured
      </label>
      <div className="flex gap-3">
        <button type="submit" className="bg-blood px-6 py-3 text-xs font-bold tracking-[0.2em] uppercase hover:bg-blood-deep">Save</button>
        <button type="button" onClick={onCancel} className="border border-white/30 px-6 py-3 text-xs font-bold tracking-[0.2em] uppercase">Cancel</button>
      </div>
    </form>
  );
}

function Inp({ label, v, on, type = "text", full = false, multi = false }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="block text-[10px] font-bold tracking-[0.2em] uppercase text-white/40 mb-1">{label}</label>
      {multi ? (
        <textarea value={v} onChange={on} rows={3} className="w-full bg-black border border-white/20 px-3 py-2 text-sm outline-none focus:border-blood" />
      ) : (
        <input type={type} value={v} onChange={on} className="w-full bg-black border border-white/20 px-3 py-2 text-sm outline-none focus:border-blood" />
      )}
    </div>
  );
}
